import { initializeTopLevelDiv, getVideoIdentifier } from '../../rrwebUtils'
import { BaseVideoPlayer } from '../players/BaseVideoPlayer'
import { ProcessedRRWebEvents, VideoStartEvent, VideoStateChangeEvent } from '../../types'
import { RECORDING_TYPES } from '@simplisafe/blueprint-web-dsl'
import { PlayerError } from '../players/PlayerError'
import { FlvPlayer } from '../players/FlvPlayer'
import { HlsPlayer } from '../players/HlsPlayer'
import { Camera } from '../../../../../../api/models'
import { VanillaPlayer } from '../players/VanillaPlayer'

/**
 * this appends the videos to the body of the player so they can preload their buffers and start playing immediately
 * their initial position is static and their box size is 0 so they will not be visible
 */
export class VideoPreloader {
  private ready: boolean = false
  private camerasById: Map<string, Camera>

  constructor(camerasById: Map<string, Camera>) {
    this.camerasById = camerasById
  }

  /**
   * We need to append the embedded videos to the body upfront so they can preload their buffers and start playing immediately
   * Their box size is 0 so they will not be visible
   * @param document
   * @param processedRRWebEvents
   * @private
   */
  public appendVideosToBodyForPreloading(document: Document, processedRRWebEvents: ProcessedRRWebEvents): Map<string, BaseVideoPlayer> {
    const embeddedVideos = new Map<string, BaseVideoPlayer>()
    for (const videoStartEvent of processedRRWebEvents.videoStartEvents) {
      const topLevelDiv = document.createElement('div')
      document.body.appendChild(topLevelDiv)
      const videoIdentifier = getVideoIdentifier(videoStartEvent)
      const videoStateChangeEvents: VideoStateChangeEvent[] = processedRRWebEvents.videoStateChangeEventsById.get(videoIdentifier) ?? []
      const video = this.createVideoPlayer(topLevelDiv, videoStartEvent, videoStateChangeEvents)
      embeddedVideos.set(videoIdentifier, video)
      initializeTopLevelDiv(topLevelDiv, video)
    }

    // emit the ready event when all the videos have finished loading
    this.setupPluginReadyEvent(embeddedVideos)

    return embeddedVideos
  }

  private setupPluginReadyEvent(embeddedVideos: Map<string, BaseVideoPlayer>) {
    if (embeddedVideos.size) {
      const readyPromises: Promise<void>[] = []
      for (const video of embeddedVideos.values()) {
        readyPromises.push(video.playerReady())
      }
      Promise.all(readyPromises).then(() => {
        this.ready = true
        this.readyCb?.()
      })
    } else {
      this.ready = true
      this.readyCb?.()
    }
  }

  private readyCb: (() => void) | undefined

  /**
   * use this to register a ready callback. The player will emit the ready event with all the embedded videos have emitted a "canplaythrough" event
   * @param ready
   */
  public onReady(ready: () => void): void {
    this.readyCb = ready
    if (this.ready) {
      this.readyCb()
    }
  }

  private createVideoPlayer(
    appendTo: HTMLElement,
    videoStartEvent: VideoStartEvent,
    videoStateChangeEvents: VideoStateChangeEvent[]
  ): BaseVideoPlayer {
    const isFlvRecording = videoStartEvent.playbackType?.endsWith(RECORDING_TYPES.FLV)
    const isVanillaVideo = videoStartEvent.playbackType === 'video'
    if (videoStartEvent.videoAccessError) {
      return new PlayerError(appendTo, videoStartEvent)
    } else if (isFlvRecording) {
      const camera = this.camerasById.get(videoStartEvent.cameraId)
      return new FlvPlayer(appendTo, videoStartEvent, videoStateChangeEvents, camera?.model)
    } else if (isVanillaVideo) {
      return new VanillaPlayer(appendTo, videoStartEvent, videoStateChangeEvents)
    } else {
      return new HlsPlayer(appendTo, videoStartEvent, videoStateChangeEvents)
    }
  }
}
